import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui-custom/Card";
import Button from "../ui-custom/Button";
import { Input } from "../ui-custom/Input";
import { Wallet, TrendingUp, TrendingDown, PiggyBank, Search, Plus, Eye, Check, X, Info, Play } from "lucide-react";
import { AllocateFundsDialog } from "./AllocateFundsDialog";
import { PettyCashApprovalDetailDialog } from "./PettyCashApprovalDetailDialog";
import { useAlert } from "../UI_components/Alert";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

function getAvatarColor(name) {
  const colors = [
    "bg-sm-accent/15 text-sm-accent",
    "bg-dark-green/15 text-dark-green",
    "bg-sm-secondary/15 text-sm-secondary",
    "bg-sm-primary/15 text-sm-primary",
    "bg-destructive/15 text-destructive",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
}

export function PettyCashOverview({
  isOwner = true,
  totalPettyCash,
  totalAllocated,
  totalSpent,
  balanceLeft,
  spendableBalance = 0,
  pendingExpenses = [],
  myRecentApprovals = [],
  employeeBalances,
  onApproveExpense,
  onRejectExpense,
  approvalActionLoadingById,
  onAllocateFunds,
  loadingTeamAllocations = false,
  loadingApprovals = false,
  onExecute,
  executeLoadingById = {},
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const { addAlert } = useAlert();

  const handleViewDetail = (approval) => {
    setSelectedApproval(approval);
    setIsDetailOpen(true);
  };

  const handleApproveClick = async (expenseId) => {
    try {
      await onApproveExpense(expenseId);
      addAlert("Successfully approved", "success");
    } catch (err) {
      console.error("Approval failed", err);
      addAlert("Failed to approve", "error");
    }
  };

  const handleRejectClick = (expenseId) => {
    addAlert("Are you sure you want to decline this expense?", "confirm", {
      onConfirm: async () => {
        try {
          await onRejectExpense(expenseId);
          addAlert("Successfully rejected", "success");
        } catch (err) {
          console.error("Rejection failed", err);
          addAlert("Failed to reject", "error");
        }
      },
    });
  };

  const safeNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  // Total spent should always be positive: allocated - balanceLeft
  const computedTotalSpent = Math.max(0, safeNumber(totalAllocated) - safeNumber(balanceLeft));

  const effectivePendingApprovalsCount = (Array.isArray(pendingExpenses) ? pendingExpenses : []).reduce(
    (acc, expense) => {
      const statusType = expense?._raw?.status_type;
      if (statusType != null) return acc + (String(statusType).toUpperCase() === "PENDING" ? 1 : 0);
      const status = expense?.status;
      if (status != null) return acc + (String(status).toUpperCase() === "PENDING" ? 1 : 0);
      return acc + 1;
    },
    0
  );

  const approvalsSubtitle = loadingApprovals
    ? "Loading pending approvals..."
    : effectivePendingApprovalsCount === 0
      ? "No expenses need your approval"
      : `${effectivePendingApprovalsCount} expense${effectivePendingApprovalsCount === 1 ? "" : "s"} ${effectivePendingApprovalsCount === 1 ? "needs" : "need"} your approval`;

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isOwnerEmployee = (emp) => {
    const roleText = [emp?.role, emp?.designation, emp?.profession, emp?.employee_profession]
      .filter(Boolean)
      .join(" ");
    return /\bowner\b/i.test(roleText);
  };

  const filteredEmployees = (employeeBalances || [])
    .filter((emp) => !isOwnerEmployee(emp))
    .filter((emp) => emp.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isOwner ? (
          <>
            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Petty Cash Remaining</p>
                    <p className="text-2xl font-bold text-primary-text mt-1">{formatCurrency(totalPettyCash)}</p>
                    <p className="text-xs text-text-gray mt-1">Total funds remaining</p>
                  </div>
                  <div className="p-2 bg-sm-primary/10 rounded-lg">
                    <Wallet className="w-5 h-5 text-sm-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Total Allocated</p>
                    <p className="text-2xl font-bold text-primary-text mt-1">{formatCurrency(totalAllocated)}</p>
                    <p className="text-xs text-text-gray mt-1">Assigned to team</p>
                  </div>
                  <div className="p-2 bg-sm-secondary/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-sm-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Total Spent</p>
                    <p className="text-2xl font-bold text-sm-accent mt-1">{formatCurrency(computedTotalSpent)}</p>
                    <p className="text-xs text-text-gray mt-1">Used by team</p>
                  </div>
                  <div className="p-2 bg-sm-accent/10 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-sm-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Team Balance</p>
                    <p className={`text-2xl font-bold mt-1 ${balanceLeft >= 0 ? "text-dark-green" : "text-destructive"}`}>
                      {balanceLeft < 0 ? "₹" : ""}{formatCurrency(Math.abs(balanceLeft))}
                    </p>
                    <p className="text-xs text-text-gray mt-1">Available funds for team</p>
                  </div>
                  <div className="p-2 bg-dark-green/10 rounded-lg">
                    <PiggyBank className="w-5 h-5 text-dark-green" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Total balance left</p>
                    <p className="text-2xl font-bold text-primary-text mt-1">{formatCurrency(balanceLeft)}</p>
                    <p className="text-xs text-text-gray mt-1">Your current wallet balance</p>
                  </div>
                  <div className="p-2 bg-sm-primary/10 rounded-lg">
                    <Wallet className="w-5 h-5 text-sm-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <div className="flex items-center gap-1.5 ">
                      <p className="text-sm text-text-gray">Your spendable balance</p>
                      <Info
                        className="w-3.5 h-3.5 text-text-gray/60 cursor-help"
                        title="This is the amount you can spend without needing any approval."
                      />
                    </div>
                    <p className="text-2xl font-bold text-sm-secondary mt-1">{formatCurrency(spendableBalance)}</p>
                    <p className="text-xs text-text-gray mt-1">Approval-free limit</p>
                  </div>
                  <div className="p-2 bg-sm-secondary/10 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-sm-secondary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Total allocated</p>
                    <p className="text-2xl font-bold text-sm-accent mt-1">{formatCurrency(totalAllocated)}</p>
                    <p className="text-xs text-text-gray mt-1">Lifetime total assigned</p>
                  </div>
                  <div className="p-2 bg-sm-accent/10 rounded-lg">
                    <PiggyBank className="w-5 h-5 text-sm-accent" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 min-h-[100px]">
                <div className="flex items-start justify-between h-full pt-4">
                  <div>
                    <p className="text-sm text-text-gray">Amount spent</p>
                    <p className={`text-2xl font-bold mt-1 text-dark-green`}>
                      {formatCurrency(totalSpent)}
                    </p>
                    <p className="text-xs text-text-gray mt-1">Total utilized so far</p>
                  </div>
                  <div className="p-2 bg-dark-green/10 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-dark-green" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Two Column Layout */}
      <div className={`grid ${isOwner ? "lg:grid-cols-2" : "grid-cols-1  mx-auto"} gap-6`}>
        {/* Pending Approvals (for Owner) or Recent Approvals (for Employee) */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-sm-accent/10 rounded">
                <Eye className="w-4 h-4 text-sm-accent" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold">
                  {isOwner ? "Pending Approvals" : "Recent Requested Approvals"}
                </CardTitle>
                <p className="text-xs text-text-gray">
                  {isOwner ? approvalsSubtitle : "Showing your top 5 most recent requests"}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {isOwner ? (
              // Owner's Pending Approvals view
              <>
                {loadingApprovals && pendingExpenses.length === 0 ? (
                  <div className="text-center py-8 text-text-gray text-sm">
                    Loading approvals...
                  </div>
                ) : pendingExpenses.length === 0 ? (
                  <div className="text-center py-8 text-text-gray text-sm">
                    No pending approvals
                  </div>
                ) : (
                  pendingExpenses.slice(0, 3).map((expense) => (
                    <div key={expense.id} className="border border-sm-border rounded-lg p-4 bg-gray-bg/20">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getAvatarColor(expense.employeeName)}`}>
                            {getInitials(expense.employeeName)}
                          </div>
                          <div>
                            <p className="font-medium text-primary-text">{expense.category}</p>
                            <p className="text-sm text-text-gray">{expense.employeeName}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-sm-primary">₹{expense.amount.toLocaleString("en-IN")}</p>
                          <span className="inline-block px-2 py-0.5 bg-sm-accent/15 text-sm-accent text-xs rounded-full">
                            Pending
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-text-gray mb-3">
                        <span>{expense.project ?? "Organization Purchase"}</span>
                        <span>•</span>
                        <span>{formatDate(expense.date)}</span>
                        {expense.attachment && (
                          <>
                            <span>•</span>
                            <span>📎 Bill attached</span>
                          </>
                        )}
                      </div>

                      {expense.remarks && (
                        <div className="bg-gray-bg/50 rounded p-2 mb-3 text-sm text-text-gray italic">
                          "{expense.remarks}"
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetail(expense)}
                          disabled={Boolean(approvalActionLoadingById?.[expense.id])}
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleApproveClick(expense.id)}
                          disabled={Boolean(approvalActionLoadingById?.[expense.id])}
                          className="bg-dark-green text-white hover:bg-dark-green/90"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          {approvalActionLoadingById?.[expense.id] ? "Processing..." : "Approve"}
                        </Button>
                        <button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRejectClick(expense.id)}
                          disabled={Boolean(approvalActionLoadingById?.[expense.id])}
                          className="text-destructive text-sm flex flex-row items-center justify-center hover:bg-red-500 hover:text-white rounded-lg px-2 py-1 transition-all duration-200"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Decline
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </>
            ) : (
              // Employee's Recent Approvals view
              <>
                {loadingApprovals && myRecentApprovals.length === 0 ? (
                  <div className="text-center py-8 text-text-gray text-sm">
                    Loading your recent approvals...
                  </div>
                ) : myRecentApprovals.length === 0 ? (
                  <div className="text-center py-8 text-text-gray text-sm">
                    You haven't requested any approvals yet.
                  </div>
                ) : (
                  myRecentApprovals.map((item, index) => {
                    const approvalId = item?.approval_id ?? item?.id;
                    const status = String(item.status_type || "PENDING").toUpperCase();
                    const statusConfig = {
                      PENDING: "bg-sm-accent/15 text-sm-accent",
                      APPROVED: "bg-dark-green/15 text-dark-green",
                      REJECTED: "bg-destructive/15 text-destructive",
                      EXECUTED: "bg-sm-primary/15 text-sm-primary",
                    };

                    const expenseLike = {
                      id: approvalId,
                      employeeName: item.requested_by?.split('|')[1]?.trim() || "You",
                      title: item.category || "Approval Request",
                      amount: Number(item.amount || 0),
                      project: item.site != null ? `${item.site_name}` : "Org",
                      date: item.created_on || new Date().toISOString(),
                      statusType: status,
                      category: item.category,
                      remarks: item.remarks,
                      _raw: item,
                    };

                    return (
                      <div key={approvalId} className="border border-sm-border rounded-lg p-4 bg-gray-bg/10 hover:bg-gray-bg/20 transition-colors">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-semibold text-xs border border-sm-border">
                              #{approvalId}
                            </div>
                            <div>
                              <p className="font-semibold text-primary-text">{item.category}</p>
                              <p className="text-xs text-text-gray">{item.payment_type || "Expense"}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-sm-primary">₹{Number(item.amount || 0).toLocaleString("en-IN")}</p>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusConfig[status] || statusConfig.PENDING}`}>
                              {status}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-text-gray mb-3">
                          <span>{item.site != null ? `${item.site_name}` : "Organization"}</span>
                          <span>•</span>
                          <span>{formatDate(item.created_on || item.approved_on)}</span>
                          {item.is_executed && (
                            <span className="text-dark-green font-medium ml-auto flex items-center gap-1">
                              <Check className="w-3 h-3" /> Executed
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs px-3"
                            onClick={() => handleViewDetail(expenseLike)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1.5" />
                            View
                          </Button>
                          {status === 'APPROVED' && !item.is_executed && (
                            <Button
                              size="sm"
                              className="h-8 text-xs px-3 bg-dark-green text-white hover:bg-dark-green/90"
                              onClick={() => onExecute(approvalId, item.category)}
                              disabled={Boolean(executeLoadingById?.[approvalId])}
                            >
                              <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
                              {executeLoadingById?.[approvalId] ? "Processing..." : "Execute"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Team Allocations (only for Owner) */}
        {isOwner && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-sm-secondary/10 rounded">
                    <TrendingUp className="w-4 h-4 text-sm-secondary" />
                  </div>
                  <CardTitle className="text-base font-semibold">Team Allocations</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-gray" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 w-32"
                    />
                  </div>
                  <Button
                    size="md"
                    onClick={() => setIsAllocateDialogOpen(true)}
                    variant="primary"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Allocate
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {loadingTeamAllocations && (
                <div className="text-center py-8 text-text-gray text-sm">Loading team allocations...</div>
              )}
              {filteredEmployees.slice(0, 4).map((emp) => (
                <div key={emp.id} className="flex items-center justify-between py-3 border-b border-sm-border last:border-0">
                  <div className="flex items-center gap-3">
                    {emp.userpic ? (
                      <img src={emp.userpic} alt={emp.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${getAvatarColor(emp.name)}`}>
                        {getInitials(emp.name)}
                      </div>
                    )}
                    <div>
                      <div className="flex flex-col items-start">
                        <span className="font-medium text-primary-text">{emp.name}</span>
                        <span className="text-xs px-2 py-0.5 bg-sm-secondary/15 text-sm-secondary rounded font-medium">
                          {emp.role}
                        </span>
                      </div>
                      {/* <p className="text-sm text-text-gray">{emp.project}</p> */}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right text-sm">
                    <div>
                      <p className="text-xs text-text-gray">Allocated</p>
                      <p className="font-medium text-primary-text">₹{emp.totalAllocated.toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-gray">Spent</p>
                      <p className="font-medium text-sm-accent">₹{(emp.totalAllocated - emp.balance).toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-gray">Balance</p>
                      <p className="font-medium text-dark-green">₹{emp.balance.toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-text-gray">Spendable</p>
                      <p className="font-medium text-sm-secondary">₹{(emp.spendableAmount || 0).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <AllocateFundsDialog
        open={isAllocateDialogOpen}
        onOpenChange={setIsAllocateDialogOpen}
        onSubmit={onAllocateFunds}
      />

      <PettyCashApprovalDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        approval={selectedApproval}
      />
    </div>
  );
}
